import React from 'react';
import { motion } from 'framer-motion';
import VisualizationRenderer from '../AISearchVisualization/AISearchVisualization';
import { parseAnswerVisuals } from './parseAnswerVisuals';

const toneClass = (tone, isDark) => {
  if (tone === 'positive') return isDark ? 'border-emerald-500/40 text-emerald-400' : 'border-emerald-500/50 text-emerald-700';
  if (tone === 'negative') return isDark ? 'border-rose-500/40 text-rose-400' : 'border-rose-500/50 text-rose-700';
  return isDark ? 'border-amber-500/40 text-amber-400' : 'border-amber-500/50 text-amber-700';
};

/**
 * Professional visual answer board — specific KPIs, people+what, insights, charts.
 */
export default function AskTgVisualAnswer({
  text = '',
  visualizations = [],
  isDark = false,
  isStreaming = false,
}) {
  const parsed = React.useMemo(
    () => parseAnswerVisuals(text, visualizations),
    [text, visualizations],
  );
  const vizList = Array.isArray(visualizations) ? visualizations : [];

  const card = isDark ? 'bg-[#1c1c1c] border border-[#2e2e2e]' : 'bg-white border border-gray-200 shadow-sm';
  const muted = isDark ? 'text-gray-500' : 'text-gray-500';
  const fg = isDark ? 'text-gray-100' : 'text-gray-900';
  const soft = isDark ? 'text-gray-400' : 'text-gray-600';
  const track = isDark ? 'bg-[#2a2a2a]' : 'bg-gray-100';
  const barFill = isDark ? 'bg-sky-500/75' : 'bg-sky-600/85';
  const surface = isDark ? 'bg-[#141414]' : 'bg-gray-50';

  const score = parsed.verdict?.score;
  const verdictTone = parsed.verdict?.tone || 'mixed';

  return (
    <div className="w-full space-y-4">
      {/* Header + verdict */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          {parsed.period && (
            <div className={`text-[10px] uppercase tracking-[0.08em] mb-1 ${muted}`}>{parsed.period}</div>
          )}
          <div className={`text-[15px] font-semibold leading-snug tracking-tight ${fg}`}>
            {parsed.headline || ((isStreaming || parsed.streaming) ? 'Building insight board…' : 'Usage report')}
            {(isStreaming || parsed.streaming) && (
              <span className="inline-block w-1.5 h-3.5 ml-1.5 bg-sky-500 animate-pulse align-middle" />
            )}
          </div>
          {parsed.call && (
            <div className={`mt-1.5 text-[12.5px] leading-snug ${soft}`}>{parsed.call}</div>
          )}
        </div>
        {parsed.verdict && (
          <div className={`flex items-center gap-2.5 rounded-xl border px-3 py-2 ${card} ${toneClass(verdictTone, isDark)}`}>
            {typeof score === 'number' && (
              <div className={`w-10 h-10 rounded-full border-2 flex items-center justify-center text-sm font-bold ${toneClass(verdictTone, isDark)}`}>
                {score}
              </div>
            )}
            <div className="min-w-0">
              <div className={`text-[10px] uppercase tracking-wider ${muted}`}>Verdict</div>
              <div className="text-[12.5px] font-semibold leading-tight">{parsed.verdict.label}</div>
            </div>
          </div>
        )}
      </div>

      {/* KPIs — only labeled ones */}
      {parsed.kpis?.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {parsed.kpis.map((k) => (
            <motion.div
              key={`${k.label}-${k.value}`}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              className={`rounded-xl px-3 py-2.5 ${card}`}
            >
              <div className={`text-[10px] uppercase tracking-wider ${muted}`}>{k.label}</div>
              <div className={`text-lg font-semibold tracking-tight mt-0.5 ${fg}`}>{k.value}</div>
            </motion.div>
          ))}
        </div>
      )}

      {/* People rank with what/signal */}
      {parsed.people?.length > 0 && (
        <div className={`rounded-xl p-3.5 ${card}`}>
          <div className={`text-[10px] uppercase tracking-wider mb-2.5 ${muted}`}>Who · how much · what</div>
          <div className="space-y-2.5">
            {parsed.people.map((p) => (
              <div key={p.name} className="grid grid-cols-[120px_1fr_auto] sm:grid-cols-[140px_1fr_minmax(0,1.2fr)_auto] gap-x-2 gap-y-0.5 items-center">
                <span className={`text-[12.5px] font-medium truncate ${fg}`}>{p.name}</span>
                <div className={`h-2 rounded-full overflow-hidden ${track}`}>
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${p.pct || 0}%` }}
                    transition={{ duration: 0.45 }}
                    className={`h-full rounded-full ${barFill}`}
                  />
                </div>
                <span className={`hidden sm:block text-[11.5px] truncate ${soft}`}>
                  {p.what || p.signal || '—'}
                </span>
                <span className={`text-[11.5px] font-medium tabular-nums text-right ${fg}`}>{p.metric}</span>
                {(p.what || p.signal) && (
                  <span className={`sm:hidden col-span-3 text-[11px] ${soft}`}>
                    {p.what}{p.signal ? ` · ${p.signal}` : ''}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Charts */}
      {vizList.map((viz, i) => (
        <motion.div
          key={`viz-${i}`}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: i * 0.04 }}
          className="w-full"
        >
          <VisualizationRenderer data={viz} isDark={isDark} />
        </motion.div>
      ))}

      {/* Insights — the "does it make sense" layer */}
      {parsed.insights?.length > 0 && (
        <div className={`rounded-xl p-3.5 ${card}`}>
          <div className={`text-[10px] uppercase tracking-wider mb-2.5 ${muted}`}>What the pattern means</div>
          <ol className="space-y-2.5 m-0 pl-0 list-none">
            {parsed.insights.map((insight, i) => (
              <li key={i} className="flex gap-2.5">
                <span className={`flex-shrink-0 w-5 h-5 rounded-md text-[10px] font-semibold flex items-center justify-center ${surface} ${soft}`}>
                  {i + 1}
                </span>
                <span className={`text-[13px] leading-snug ${fg}`}>{insight}</span>
              </li>
            ))}
          </ol>
        </div>
      )}

      {parsed.risks?.length > 0 && (
        <div className={`rounded-xl p-3.5 border ${isDark ? 'bg-rose-500/5 border-rose-500/25' : 'bg-rose-50 border-rose-200'}`}>
          <div className={`text-[10px] uppercase tracking-wider mb-2 ${isDark ? 'text-rose-400' : 'text-rose-700'}`}>Watch</div>
          <ul className="space-y-1.5 m-0 pl-4">
            {parsed.risks.map((r, i) => (
              <li key={i} className={`text-[12.5px] leading-snug ${soft}`}>{r}</li>
            ))}
          </ul>
        </div>
      )}

      {(isStreaming || parsed.streaming) && !parsed.insights?.length && !parsed.people?.length && !vizList.length && (
        <div className={`h-20 rounded-xl animate-pulse ${track}`} />
      )}

      {!parsed.streaming && !parsed.insights?.length && !parsed.people?.length && !vizList.length && !isStreaming && parsed.text && !/^\s*[{["]/.test(parsed.text) && (
        <div className={`rounded-xl p-3.5 text-[13px] leading-relaxed whitespace-pre-wrap ${card} ${fg}`}>
          {parsed.text.slice(0, 900)}
        </div>
      )}
    </div>
  );
}
